import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
} from '@chakra-ui/react';
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { StripeError } from 'types/stripe';

interface CreditCardFormProps {
  onSubmit: (paymentMethodId: string) => Promise<void>;
  isLoading?: boolean;
  buttonText?: string;
  customerEmail?: string;
  customerName?: string;
  clientSecret?: string;
  elementOptions?: any; // Custom PaymentElement options
}

export const CreditCardForm: React.FC<CreditCardFormProps> = ({
  onSubmit,
  isLoading = false,
  buttonText = 'Submit Payment',
  customerEmail: _customerEmail,
  customerName: _customerName,
  clientSecret,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // For PaymentElement, we use elements.submit() to validate
      const { error: submitError } = await elements.submit();

      if (submitError) {
        setError(submitError.message || 'Payment validation failed');
        toast({
          title: 'Payment Error',
          description: submitError.message || 'Payment validation failed',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (clientSecret) {
        // Determine the type of intent based on the client secret
        const isSetupIntent = clientSecret.startsWith('seti_');
        const isPaymentIntent = clientSecret.startsWith('pi_');

        if (isPaymentIntent) {
          // For purchases, use confirmPayment with Payment Intent
          const { paymentIntent, error: paymentError } =
            await stripe.confirmPayment({
              clientSecret,
              elements,
              redirect: 'if_required',
              confirmParams: {
                return_url: window.location.origin + '/payment-success',
              },
            });

          if (paymentError) {
            setError(paymentError.message || 'Payment failed');
            toast({
              title: 'Payment Error',
              description: paymentError.message || 'Payment failed',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          } else if (paymentIntent) {
            // The payment method ID is available in the payment intent
            const paymentMethodId = paymentIntent.payment_method as string;
            await onSubmit(paymentMethodId);
            toast({
              title: 'Payment Successful',
              description: 'Your payment has been processed successfully',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
          }
        } else if (isSetupIntent) {
          // For saving payment methods, use confirmSetup with Setup Intent
          const { setupIntent, error: setupError } = await stripe.confirmSetup({
            clientSecret,
            elements,
            redirect: 'if_required',
            confirmParams: {
              return_url: window.location.origin + '/account',
            },
          });

          if (setupError) {
            setError(setupError.message || 'Payment method setup failed');
            toast({
              title: 'Payment Error',
              description: setupError.message || 'Payment method setup failed',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          } else if (setupIntent) {
            // The payment method ID is available in the setup intent
            const paymentMethodId = setupIntent.payment_method as string;
            await onSubmit(paymentMethodId);
            toast({
              title: 'Payment Method Added',
              description: 'Your payment method has been added successfully',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
          }
        }
      }
    } catch (err) {
      const error = err as StripeError;
      setError(error.message || 'An unexpected error occurred');
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertTitle>Payment Error!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <FormControl>
          <FormLabel>Payment Information</FormLabel>
          <Box
            border="1px"
            borderColor="gray.300"
            borderRadius="md"
            p={3}
            _focusWithin={{
              borderColor: 'brand.400',
              boxShadow: '0 0 0 1px var(--chakra-colors-brand-400)',
            }}
          >
            <PaymentElement />
          </Box>
        </FormControl>

        <Button
          type="submit"
          bg="brand.400"
          color="black"
          _hover={{ bg: 'brand.300' }}
          size="lg"
          borderRadius="full"
          isLoading={processing || isLoading}
          loadingText="Processing..."
          disabled={!stripe || processing || isLoading}
          w="full"
        >
          {buttonText}
        </Button>
      </VStack>
    </Box>
  );
};
